import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import { ExtensionDefinition } from '@backstage/frontend-plugin-api';
import {
  NavContentBlueprint,
  type NavContentComponentProps,
} from '@backstage/plugin-app-react';
import { catalogLocationReadPermission } from '@backstage/plugin-catalog-common/alpha';
import { usePermission } from '@backstage/plugin-permission-react';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { useDamAccess } from '@elx-dx/plugin-dam';
import { useAgentAccess } from '@elx-dx/phoenix-agent-runtime';
import { useFileStoreAccess } from '@elx-dx/plugin-file-store';
import { useHaclMapperAccess } from '@elx-dx/plugin-hacl-mapper-message-conversion';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ListIcon from '@material-ui/icons/FormatListBulleted';
import MemoryIcon from '@material-ui/icons/Memory';
import MenuIcon from '@material-ui/icons/Menu';
import StorageIcon from '@material-ui/icons/Storage';
import { SidebarLogo } from './SidebarLogo';

export function SidebarNavigation({ navItems }: NavContentComponentProps) {
  const { allowed, loading } = useFileStoreAccess();
  const haclAccess = useHaclMapperAccess();
  const damAccess = useDamAccess();
  const agentAccess = useAgentAccess();
  const { allowed: catalogAllowed, loading: catalogLoading } = usePermission({
    permission: catalogLocationReadPermission,
  });
  const items = navItems.clone();
  const catalog = items.take('page:catalog');
  const hacl = items.take('page:hacl-mapper-message-conversion');
  const fileStore = items.take('page:file-store');
  const pdRelease = items.take('page:dam/pd-release-management');
  const agent = items.take('page:agent');
  const nav = items.withComponent(item => (
    <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
  ));
  const settings = nav.take('page:user-settings');
  const showHacl =
    !haclAccess.loading && haclAccess.allowed && hacl !== undefined;
  const showFileStore = !loading && allowed && fileStore !== undefined;

  return (
    <Sidebar>
      <SidebarLogo />
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        <SidebarScrollWrapper>
          {(showHacl || showFileStore) && (
            <SidebarItem icon={MemoryIcon} text="IoT">
              <SidebarSubmenu title="IoT Platform">
                {showHacl && (
                  <SidebarSubmenuItem
                    icon={AssignmentIcon}
                    title={hacl.title}
                    to={hacl.href}
                  />
                )}
                {showFileStore && (
                  <SidebarSubmenuItem
                    icon={ListIcon}
                    title={fileStore.title}
                    to={fileStore.href}
                  />
                )}
              </SidebarSubmenu>
            </SidebarItem>
          )}
          {!damAccess.loading && damAccess.allowed && pdRelease && (
            <SidebarItem icon={StorageIcon} text="DAM">
              <SidebarSubmenu title="DAM Data Model">
                <SidebarSubmenuItem
                  icon={ListIcon}
                  title={pdRelease.title}
                  to={pdRelease.href}
                />
              </SidebarSubmenu>
            </SidebarItem>
          )}
          {!catalogLoading && catalogAllowed && catalog && (
            <SidebarItem
              icon={() => catalog.icon}
              to={catalog.href}
              text={catalog.title}
            />
          )}
          {!agentAccess.loading && agentAccess.allowed && agent && (
            <SidebarItem
              icon={() => agent.icon}
              to={agent.href}
              text={agent.title}
            />
          )}
          {nav.rest({ sortBy: 'title' })}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarGroup
        label="Settings"
        icon={<UserSettingsSignInAvatar />}
        to="/settings"
      >
        {settings}
      </SidebarGroup>
    </Sidebar>
  );
}

export const SidebarContent: ExtensionDefinition = NavContentBlueprint.make({
  params: { component: SidebarNavigation },
});
